import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft, ArrowRight, ArrowUpRight, Check, Zap, Cpu, Clock, CalendarCheck, Scale,
  Calculator, FileSearch, BadgeCheck, GitCompareArrows, PackageSearch, LineChart, ClipboardList,
  SlidersHorizontal, Award, Wallet, Target, Globe, RefreshCw, Gauge,
} from 'lucide-react';
import { MANUFACTURING_PAGE, CONTACT } from '../mock';
import Navbar from './Navbar';
import SiteFooter from './SiteFooter';

const ICONS = {
  Cpu, Clock, CalendarCheck, Scale, Calculator, FileSearch, BadgeCheck, GitCompareArrows,
  PackageSearch, LineChart, ClipboardList, SlidersHorizontal, Award, Wallet, Target, Globe, RefreshCw, Gauge,
};

export default function Manufacturing() {
  const { hero, intro, values, services, whyOutsource, outro } = MANUFACTURING_PAGE;

  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'Contract Manufacturing — Electronico Holdings';
  }, []);

  return (
    <div className="relative min-h-screen bg-[#0a1526] text-white">
      <Navbar />

      {/* HERO */}
      <section className="relative pt-36 pb-20 px-5 overflow-hidden grid-bg">
        <div className="blob bg-[#088ffe] w-[460px] h-[460px] -top-24 -left-24 animate-pulse-glow" />
        <div className="blob bg-[#0a6bd6] w-[360px] h-[360px] top-20 right-0 opacity-40" />
        <div className="relative z-10 max-w-7xl mx-auto grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full neon-border px-4 py-1.5 mb-6">
              <Cpu className="h-4 w-4 text-[#088ffe]" />
              <span className="font-mono-accent text-xs text-white/80">{hero.kicker}</span>
            </div>
            <h1 className="font-display text-4xl md:text-6xl font-bold leading-[1.08] mb-6">
              Electronic Manufacturing, <span className="gradient-text animate-gradient">Done Right</span>
            </h1>
            <p className="text-lg text-white/65 max-w-xl mb-8 leading-relaxed">{hero.subtitle}</p>
            <div className="flex flex-wrap gap-4">
              <a href="#services" className="group inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-[#088ffe] to-[#0a6bd6] text-black px-7 py-3.5 font-semibold hover:shadow-[0_0_30px_rgba(8,143,254,0.5)] transition-shadow">
                Our Services <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
              </a>
              <a href="#mfg-cta" className="inline-flex items-center gap-2 rounded-xl neon-border px-7 py-3.5 font-semibold text-white hover:bg-white/5 transition-colors">
                Request a Quote
              </a>
            </div>
            <div className="grid grid-cols-4 gap-4 mt-12 max-w-xl">
              {hero.stats.map((s) => (
                <div key={s.label}>
                  <div className="font-display text-xl md:text-2xl font-bold text-glow-magenta">{s.value}</div>
                  <div className="text-xs text-white/50 mt-1">{s.label}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="relative animate-floaty">
            <div className="absolute -inset-4 bg-gradient-to-tr from-[#088ffe]/30 to-[#0a6bd6]/30 blur-2xl rounded-3xl" />
            <div className="relative rounded-3xl overflow-hidden neon-border">
              <img src={hero.image} alt="Electronic contract manufacturing" className="w-full h-[440px] object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0a1526] via-transparent to-transparent" />
              <div className="absolute bottom-5 left-5 right-5 flex items-center justify-between">
                <span className="font-mono-accent text-xs text-white">// LINE ACTIVE</span>
                <span className="flex items-center gap-2 text-xs text-white/70">
                  <span className="h-2 w-2 rounded-full bg-[#5bd0ff] animate-pulse-glow" /> Producing
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* INTRO + VALUES */}
      <section className="relative py-20 px-5">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-14 max-w-3xl mx-auto">
            <span className="font-mono-accent text-xs text-[#088ffe]">// WHY ELECTRONICO</span>
            <p className="text-xl md:text-2xl font-display font-medium text-white/85 mt-4 leading-snug">{intro}</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((v) => {
              const Icon = ICONS[v.icon];
              return (
                <div key={v.title} className="group neon-border rounded-3xl p-7 hover:-translate-y-2 transition-transform duration-300">
                  <div className="h-14 w-14 rounded-2xl bg-gradient-to-br from-[#088ffe]/20 to-[#0a6bd6]/20 flex items-center justify-center mb-5 group-hover:glow-magenta transition-shadow">
                    <Icon className="h-7 w-7 text-[#088ffe]" />
                  </div>
                  <h3 className="font-display text-lg font-bold mb-2">{v.title}</h3>
                  <p className="text-sm text-white/55 leading-relaxed">{v.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section id="services" className="relative py-20 px-5 grid-bg">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <span className="font-mono-accent text-xs text-[#088ffe]">// SERVICES OFFERED</span>
            <h2 className="font-display text-4xl md:text-5xl font-bold mt-3">End-to-End Manufacturing Services</h2>
            <p className="text-white/55 mt-4 max-w-xl mx-auto">A systematic approach with thorough data verification — for timely, high-quality electronics and a smooth production process.</p>
          </div>

          <div className="space-y-8">
            {services.map((s, i) => {
              const Icon = ICONS[s.icon];
              const flip = i % 2 === 1;
              return (
                <div
                  key={s.id}
                  id={s.id}
                  className="group grid lg:grid-cols-2 gap-8 items-center neon-border rounded-3xl p-6 md:p-8 hover:border-[#088ffe]/50 hover:shadow-[0_0_40px_rgba(8,143,254,0.18)] transition-all duration-300 scroll-mt-28"
                >
                  <div className={`relative h-64 md:h-80 rounded-2xl overflow-hidden ${flip ? 'lg:order-2' : ''}`}>
                    <img src={s.image} alt={s.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0d0d16]/70 to-transparent" />
                    <span className="absolute top-4 left-4 font-display text-5xl font-bold text-white/20">{s.no}</span>
                  </div>
                  <div className={flip ? 'lg:order-1' : ''}>
                    <div className="h-14 w-14 rounded-2xl bg-[#088ffe]/10 flex items-center justify-center mb-5 group-hover:glow-magenta transition-shadow">
                      <Icon className="h-7 w-7 text-[#088ffe]" />
                    </div>
                    <h3 className="font-display text-2xl md:text-3xl font-bold mb-3">{s.title}</h3>
                    <p className="text-white/60 leading-relaxed mb-6">{s.desc}</p>
                    <div className="grid sm:grid-cols-2 gap-3">
                      {s.points.map((p) => (
                        <div key={p} className="flex items-center gap-2.5 text-sm text-white/80">
                          <span className="flex h-5 w-5 items-center justify-center rounded-md bg-[#088ffe]/15 shrink-0">
                            <Check className="h-3.5 w-3.5 text-[#088ffe]" />
                          </span>
                          {p}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* WHY OUTSOURCE */}
      <section className="relative py-20 px-5">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <span className="font-mono-accent text-xs text-[#0a6bd6]">// WHY OUTSOURCE</span>
            <h2 className="font-display text-4xl md:text-5xl font-bold mt-3">Seven Reasons to Partner With Us</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {whyOutsource.map((w, i) => {
              const Icon = ICONS[w.icon];
              return (
                <div key={w.title} className="group neon-border rounded-2xl p-6 flex items-start gap-4 hover:border-[#088ffe]/50 hover:bg-white/[0.03] transition-all">
                  <span className="font-mono-accent text-2xl font-bold gradient-text shrink-0">{String(i + 1).padStart(2, '0')}</span>
                  <div>
                    <Icon className="h-6 w-6 text-[#088ffe] mb-2 group-hover:scale-110 transition-transform" />
                    <h3 className="font-display font-semibold leading-tight">{w.title}</h3>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section id="mfg-cta" className="relative py-20 px-5 grid-bg">
        <div className="max-w-5xl mx-auto">
          <div className="relative neon-border rounded-3xl p-10 md:p-16 overflow-hidden text-center">
            <div className="blob bg-[#088ffe] w-80 h-80 -top-20 -left-10 opacity-30" />
            <div className="blob bg-[#0a6bd6] w-80 h-80 -bottom-20 -right-10 opacity-30" />
            <div className="relative z-10">
              <h2 className="font-display text-3xl md:text-5xl font-bold mb-4">Ready to Outsource Your Manufacturing?</h2>
              <p className="text-white/60 max-w-2xl mx-auto mb-8">{outro}</p>
              <div className="flex flex-wrap justify-center gap-4">
                <a href={`mailto:${CONTACT.email}`} className="group inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-[#088ffe] to-[#0a6bd6] text-black px-7 py-3.5 font-semibold hover:shadow-[0_0_30px_rgba(8,143,254,0.5)] transition-shadow">
                  Request a Quote <ArrowUpRight className="h-5 w-5 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                </a>
                <Link to="/" className="inline-flex items-center gap-2 rounded-xl neon-border px-7 py-3.5 font-semibold text-white hover:bg-white/5 transition-colors">
                  <ArrowLeft className="h-4 w-4" /> All Divisions
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <SiteFooter />
    </div>
  );
}
